'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Building2, Users } from 'lucide-react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { ContactEntity, toContactEntityData } from '@/components/ui/design-system/entities/contact';
import { ContactGroupBlocks } from '@/app/dashboard/components/contact/ContactGroupBlocks';
import { ConnectedOutreachDialog } from '@/app/dashboard/components/outreach/ConnectedOutreachDialog';
import { ContactQuickViewDialog } from '@/app/dashboard/components/contact/ContactQuickViewDialog';
import { groupContactsByCategory } from '@/lib/contacts/contact-groups';
import { profileToContact } from '@/lib/contacts/profile-to-contact';
import type { Contact } from '@/types/report-types';
import type { UserProfile, QueryUsersParams } from '@/actions/ContactsLookup';

const CACHE_KEY_PREFIX = 'co.brief.company_contacts.v1:';
const CACHE_TTL_MS = 60 * 60 * 1000;
const CONTACTS_PER_COMPANY = 6;
const LOOKUP_SOURCE = 'mixrank_person2_api';
const LOOKUP_DB_TIMEOUT_MS = 60_000;

interface CacheEntry {
  cachedAt: number;
  contacts: Contact[];
}

function readCache(company: string): Contact[] | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY_PREFIX + company.trim().toLowerCase());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed?.cachedAt || Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
    return Array.isArray(parsed.contacts) ? parsed.contacts : null;
  } catch {
    return null;
  }
}

function writeCache(company: string, contacts: Contact[]) {
  try {
    const entry: CacheEntry = { cachedAt: Date.now(), contacts };
    window.localStorage.setItem(CACHE_KEY_PREFIX + company.trim().toLowerCase(), JSON.stringify(entry));
  } catch {
    // quota exceeded — not fatal
  }
}

async function lookupDecisionMakers(company: string, signal?: AbortSignal): Promise<Contact[]> {
  const params: QueryUsersParams = {
    source: LOOKUP_SOURCE,
    customFilters: {
      company,
      isCurrentCompany: true,
      useDecisionMakerTitles: true,
    },
    limit: CONTACTS_PER_COMPANY,
    sortBy: 'updated_at',
    sortOrder: 'desc',
    dbQueryTimeout: LOOKUP_DB_TIMEOUT_MS,
    attachmentConfig: {
      fetchEmails: true,
      fetchPhones: false,
      fetchExperience: true,
      fetchEducation: false,
    },
  };

  const response = await fetch('/api/dashboard/contacts/lookup', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode: 'single', params }),
    signal,
  });

  if (!response.ok) return [];
  const result = await response.json();
  const profiles: UserProfile[] = result?.data?.data ?? [];
  return profiles.map(profileToContact);
}

interface CompanyContactsFlatProps {
  companyNames: string[];
}

interface CompanyContactsState {
  status: 'loading' | 'loaded' | 'error';
  contacts: Contact[];
}

export function CompanyContactsFlat({ companyNames }: CompanyContactsFlatProps) {
  const router = useRouter();

  const normalized = useMemo(() => {
    const seen = new Set<string>();
    return companyNames
      .map((n) => n.trim())
      .filter((n) => {
        if (!n) return false;
        const key = n.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  }, [companyNames]);

  const [byCompany, setByCompany] = useState<Record<string, CompanyContactsState>>({});
  const [outreachContact, setOutreachContact] = useState<Contact | null>(null);
  const [quickViewContact, setQuickViewContact] = useState<Contact | null>(null);

  const navigateToContacts = useCallback(() => {
    router.push('/d/contacts');
  }, [router]);

  useEffect(() => {
    const controllers: AbortController[] = [];

    for (const company of normalized) {
      const cached = readCache(company);
      if (cached) {
        setByCompany((prev) => ({
          ...prev,
          [company]: { status: 'loaded', contacts: cached },
        }));
        continue;
      }

      setByCompany((prev) => ({
        ...prev,
        [company]: { status: 'loading', contacts: [] },
      }));

      const controller = new AbortController();
      controllers.push(controller);
      void (async () => {
        try {
          const contacts = await lookupDecisionMakers(company, controller.signal);
          if (controller.signal.aborted) return;
          writeCache(company, contacts);
          setByCompany((prev) => ({
            ...prev,
            [company]: { status: 'loaded', contacts },
          }));
        } catch {
          if (controller.signal.aborted) return;
          setByCompany((prev) => ({
            ...prev,
            [company]: { status: 'error', contacts: [] },
          }));
        }
      })();
    }

    return () => controllers.forEach((c) => c.abort());
  }, [normalized]);

  const hasAnyContacts = Object.values(byCompany).some(
    (s) => s.status === 'loaded' && s.contacts.length > 0,
  );
  const isLoading = Object.values(byCompany).some((s) => s.status === 'loading');

  if (!hasAnyContacts && !isLoading) return null;

  return (
    <>
      <div className="space-y-5">
        {normalized.map((company) => {
          const state = byCompany[company];
          if (!state || state.status === 'error') return null;
          if (state.status === 'loading') {
            return (
              <div key={company} className="space-y-2" style={{ minHeight: 160 }}>
                <CompanyLabel name={company} />
                <div className="flex items-center gap-2 py-2">
                  <ArrowPathIcon className="h-3 w-3 animate-spin text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">Loading contacts…</span>
                </div>
              </div>
            );
          }
          if (state.contacts.length === 0) return null;
          return (
            <div key={company} className="space-y-2">
              <CompanyLabel name={company} />
              <ContactGroupBlocks
                groups={groupContactsByCategory(state.contacts, (contact) => ({
                  contactGroupKey: contact.contactGroupKey,
                  contactGroupLabel: contact.contactGroupLabel,
                  title: contact.title,
                  tags: contact.tags,
                  seniority: contact.seniority,
                }))}
                renderContact={(contact) => (
                  <ContactEntity
                    contact={toContactEntityData(contact)}
                    layout="row"
                    fields={{ email: true, linkedIn: true, location: true, tags: true, relevance: false, seniority: false, status: true }}
                    actions={{ viewProfile: true, reachOut: true }}
                    onViewProfile={() => setQuickViewContact(contact)}
                    onReachOut={() => setOutreachContact(contact)}
                    onStatusChange={() => navigateToContacts()}
                  />
                )}
                progressiveDisclosure
                initialVisibleCount={2}
                visibleStep={2}
              />
            </div>
          );
        })}
      </div>

      <ConnectedOutreachDialog
        open={outreachContact !== null}
        onOpenChange={(open) => { if (!open) setOutreachContact(null); }}
        contact={outreachContact}
      />
      <ContactQuickViewDialog
        open={quickViewContact !== null}
        onOpenChange={(open) => { if (!open) setQuickViewContact(null); }}
        contact={quickViewContact}
        onReachOut={(contact) => {
          setQuickViewContact(null);
          setOutreachContact(contact);
        }}
      />
    </>
  );
}

function CompanyLabel({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-1.5">
      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
      <p className="text-xs font-semibold text-muted-foreground">
        {name}
      </p>
    </div>
  );
}
